import axios from 'axios'
import { apiClient } from './client'
import type {
  ComplianceStandard,
  CompliancePolicy,
  TenantStandardAssignment,
  ComplianceEvaluation,
  ComplianceViolation,
  TenantPolicyWaiver,
  ComplianceSummary,
  ComplianceTrendPoint,
  ScoreEvent,
  ControlChange,
  AssignStandardRequest,
  UpdateTenantStandardRequest,
  CreateWaiverRequest,
  UpdateWaiverRequest,
  ViolationFilters,
  OpaHealthStatus,
  EvaluationResult,
  PaginatedResponse,
  ViolationSummaryResponse,
  FrameworkScore,
  FrameworkScoreDisplay,
  WorkloadCollectionHealth,
  ApiResponse,
} from '@/types'

/**
 * Standards Compliance APIs (OPA-based multi-framework evaluation) + the
 * violation summary and OPA health reads. Split out of `lib/api.ts`
 * (WO-10.4).
 */

/** Loose wire shape read by the collection-health normalizer below — the
 * Container serializes camelCase (`Program.cs` JSON options), but these
 * reads defensively accept a PascalCase payload too (pre-existing pattern,
 * preserved as-is). */
interface CollectionHealthWire {
  Workload?: string
  workload?: string
  Health?: {
    GraphCollected?: boolean
    PowerShellCollected?: boolean
    GraphExpected?: boolean
    PowerShellExpected?: boolean
    GraphCollectedAt?: string | null
    PowerShellCollectedAt?: string | null
    Errors?: string[]
  }
  health?: {
    graphCollected?: boolean
    powerShellCollected?: boolean
    graphExpected?: boolean
    powerShellExpected?: boolean
    graphCollectedAt?: string | null
    powerShellCollectedAt?: string | null
    errors?: string[]
  }
}

/**
 * Shared collection-health row mapper for `getComplianceScores` and
 * `getViolationSummary` (both echo the same `CollectionHealthDto[]`).
 *
 * BUGFIX (found while typing this return against `WorkloadCollectionHealth`):
 * the pre-split code built this object without `graphExpected` /
 * `powerShellExpected`, which are REQUIRED fields the backend has always
 * sent (`CollectionHealthDto.cs:17,23`) and that `BestPracticeAnalyzerTab`/
 * `workloadMapping.ts` actively branch on ("Identity is Graph-only, so
 * PowerShellExpected=false"). Under `any` this silently compiled to
 * `undefined`, which reads as falsy — so the "missing data" banners driven
 * by these two flags could never fire for data that came through this path.
 * Wiring them through (same PascalCase/camelCase defensive read as every
 * sibling field here) is a one-line, same-shape fix, not a behavior change
 * to the endpoint itself.
 */
function normalizeCollectionHealth(h: CollectionHealthWire): WorkloadCollectionHealth {
  return {
    workload: h.Workload ?? h.workload ?? '',
    health: {
      graphCollected: h.Health?.GraphCollected ?? h.health?.graphCollected ?? false,
      powerShellCollected: h.Health?.PowerShellCollected ?? h.health?.powerShellCollected ?? false,
      graphExpected: h.Health?.GraphExpected ?? h.health?.graphExpected ?? true,
      powerShellExpected: h.Health?.PowerShellExpected ?? h.health?.powerShellExpected ?? true,
      graphCollectedAt: h.Health?.GraphCollectedAt ?? h.health?.graphCollectedAt ?? null,
      powerShellCollectedAt: h.Health?.PowerShellCollectedAt ?? h.health?.powerShellCollectedAt ?? null,
      errors: h.Health?.Errors ?? h.health?.errors ?? [],
    },
  }
}

// --- Standards Management ---

/**
 * Get all available compliance standards/frameworks
 */
export async function getComplianceStandards(): Promise<ComplianceStandard[]> {
  try {
    const response = await apiClient.get<ApiResponse<ComplianceStandard[]>>('/compliance/standards')
    return response.data.data || response.data || []
  } catch (error: unknown) {
    console.error('Failed to fetch compliance standards:', error)
    return []
  }
}

/**
 * Get a specific compliance standard by ID
 */
export async function getComplianceStandard(standardId: string): Promise<ComplianceStandard | null> {
  try {
    const response = await apiClient.get<ApiResponse<ComplianceStandard>>(`/compliance/standards/${standardId}`)
    return response.data.data || response.data || null
  } catch (error: unknown) {
    if (axios.isAxiosError(error) && error.response?.status === 404) {
      return null
    }
    throw error
  }
}

/**
 * Get all policies within a specific standard
 */
export async function getStandardPolicies(standardId: string): Promise<CompliancePolicy[]> {
  try {
    const response = await apiClient.get<ApiResponse<CompliancePolicy[]>>(`/compliance/standards/${standardId}/policies`)
    return response.data.data || response.data || []
  } catch (error: unknown) {
    console.error('Failed to fetch standard policies:', error)
    return []
  }
}

// --- Tenant Standards Configuration ---

interface TenantStandardWire {
  Id?: string; id?: string
  Code?: string; code?: string
  Name?: string; name?: string
  IsEnabled?: boolean; isEnabled?: boolean
  Weight?: number; weight?: number
  IncludeInGuardianScore?: boolean; includeInGuardianScore?: boolean
  AssignedAt?: string; assignedAt?: string
  AssignedBy?: string; assignedBy?: string
}

/**
 * Get standards assigned to a tenant
 * Transforms backend TenantStandardDto to frontend TenantStandardAssignment
 */
export async function getTenantStandards(tenantId: string): Promise<TenantStandardAssignment[]> {
  try {
    const response = await apiClient.get<ApiResponse<TenantStandardWire[]>>(`/compliance/tenant/${tenantId}/standards`)
    const data = response.data.data || response.data || []

    // Transform backend DTO (Code, Name) to frontend type (standardCode, standardName)
    return data.map((s) => ({
      id: s.Id ?? s.id ?? '',
      tenantId: tenantId,
      standardId: s.Id ?? s.id ?? '',
      standardCode: s.Code ?? s.code ?? '',
      standardName: s.Name ?? s.name ?? '',
      isEnabled: s.IsEnabled ?? s.isEnabled ?? false,
      weight: s.Weight ?? s.weight ?? 50,
      includeInGuardianScore: s.IncludeInGuardianScore ?? s.includeInGuardianScore ?? false,
      assignedAt: s.AssignedAt ?? s.assignedAt ?? '',
      assignedBy: s.AssignedBy ?? s.assignedBy ?? ''
    }))
  } catch (error: unknown) {
    console.error('Failed to fetch tenant standards:', error)
    return []
  }
}

/**
 * Assign a standard to a tenant
 */
export async function assignTenantStandard(tenantId: string, data: AssignStandardRequest): Promise<TenantStandardAssignment> {
  const response = await apiClient.post<ApiResponse<TenantStandardAssignment>>(
    `/compliance/tenant/${tenantId}/standards`,
    data
  )
  return response.data.data || response.data
}

/**
 * Update a tenant's standard configuration (weight, enabled, etc.)
 */
export async function updateTenantStandard(
  tenantId: string,
  standardId: string,
  data: UpdateTenantStandardRequest
): Promise<TenantStandardAssignment> {
  const response = await apiClient.put<ApiResponse<TenantStandardAssignment>>(
    `/compliance/tenant/${tenantId}/standards/${standardId}`,
    data
  )
  return response.data.data || response.data
}

/**
 * Bulk update tenant standard configurations
 */
export async function bulkUpdateTenantStandards(
  tenantId: string,
  updates: Array<{ standardId: string } & UpdateTenantStandardRequest>
): Promise<TenantStandardAssignment[]> {
  const response = await apiClient.put<ApiResponse<TenantStandardAssignment[]>>(
    `/compliance/tenant/${tenantId}/standards/bulk`,
    { updates }
  )
  return response.data.data || response.data || []
}

/**
 * Toggle whether a standard is included in the Guardian composite score.
 * Uses the existing update endpoint, preserving current isEnabled and weight values.
 */
export async function toggleStandardIncludeInScore(
  tenantId: string,
  standardId: string,
  data: UpdateTenantStandardRequest
): Promise<TenantStandardAssignment> {
  const response = await apiClient.put<ApiResponse<TenantStandardAssignment>>(
    `/compliance/tenant/${tenantId}/standards/${standardId}`,
    data
  )
  return response.data.data || response.data
}

// --- Evaluations ---

/**
 * Trigger a compliance evaluation for a tenant
 */
export async function triggerComplianceEvaluation(tenantId: string, options?: { standardIds?: string[]; forceRefresh?: boolean }): Promise<EvaluationResult> {
  const response = await apiClient.post<ApiResponse<EvaluationResult>>(
    `/compliance/tenant/${tenantId}/evaluate`,
    options || {},
    { timeout: 300000 } // 5 minute timeout - evaluation includes Graph API + PowerShell data collection
  )
  return response.data.data || response.data
}

/**
 * Get all evaluations for a tenant
 */
export async function getComplianceEvaluations(tenantId: string): Promise<ComplianceEvaluation[]> {
  try {
    const response = await apiClient.get<ApiResponse<ComplianceEvaluation[]>>(`/compliance/tenant/${tenantId}/evaluations`)
    return response.data.data || response.data || []
  } catch (error: unknown) {
    console.error('Failed to fetch evaluations:', error)
    return []
  }
}

/**
 * Get a specific evaluation by ID
 */
export async function getComplianceEvaluation(tenantId: string, evaluationId: string): Promise<ComplianceEvaluation | null> {
  try {
    const response = await apiClient.get<ApiResponse<ComplianceEvaluation>>(
      `/compliance/tenant/${tenantId}/evaluations/${evaluationId}`
    )
    return response.data.data || response.data || null
  } catch (error: unknown) {
    if (axios.isAxiosError(error) && error.response?.status === 404) {
      return null
    }
    throw error
  }
}

// --- Scores ---

interface StandardScoreWire {
  StandardId?: string; standardId?: string
  StandardCode?: string; standardCode?: string
  StandardName?: string; standardName?: string
  RawScore?: number; rawScore?: number
  AdjustedScore?: number; adjustedScore?: number
  WeightedScore?: number; weightedScore?: number
  Weight?: number; weight?: number
  PassedControls?: number; passedControls?: number
  FailedControls?: number; failedControls?: number
  WaivedControls?: number; waivedControls?: number
  DataUnavailableControls?: number; dataUnavailableControls?: number
  PartialControls?: number; partialControls?: number
  TotalControls?: number; totalControls?: number
  LastEvaluatedAt?: string; lastEvaluatedAt?: string
  IsEnabled?: boolean; isEnabled?: boolean
  IncludeInGuardianScore?: boolean; includeInGuardianScore?: boolean
}

/**
 * Get current compliance scores for a tenant (summary with framework scores)
 * Maps backend ComplianceScoreDto to frontend ComplianceSummary
 */
export async function getComplianceScores(tenantId: string): Promise<ComplianceSummary | null> {
  try {
    const response = await apiClient.get<ApiResponse<Record<string, unknown>>>(`/compliance/tenant/${tenantId}/scores`)
    const data = response.data.data || response.data

    if (!data) return null

    // Transform backend StandardScores to include display-friendly aliases.
    //
    // BUGFIX (found while typing this against FrameworkScore): the pre-split
    // code never read DataUnavailableControls/PartialControls, both of which
    // are REQUIRED fields the backend has always sent
    // (ComplianceScoreDto.cs:57-58) and that `useComplianceData.ts:570-571`
    // already reads with a `?? 0` fallback — under `any` those two fields
    // were always `undefined`, so the fallback always fired and the BPA UI
    // has been showing 0 data-unavailable/partial counts regardless of the
    // real evaluation result. Wiring them through fixes that silently.
    const standardScoresRaw = ((data.StandardScores ?? data.standardScores) as StandardScoreWire[] | undefined) ?? []
    const standardScores: FrameworkScore[] = standardScoresRaw.map((s) => ({
      standardId: s.StandardId ?? s.standardId ?? '',
      standardCode: s.StandardCode ?? s.standardCode ?? '',
      standardName: s.StandardName ?? s.standardName ?? '',
      rawScore: s.RawScore ?? s.rawScore ?? 0,
      adjustedScore: s.AdjustedScore ?? s.adjustedScore ?? 0,
      weightedScore: s.WeightedScore ?? s.weightedScore ?? 0,
      weight: s.Weight ?? s.weight ?? 0,
      passedControls: s.PassedControls ?? s.passedControls ?? 0,
      failedControls: s.FailedControls ?? s.failedControls ?? 0,
      waivedControls: s.WaivedControls ?? s.waivedControls ?? 0,
      dataUnavailableControls: s.DataUnavailableControls ?? s.dataUnavailableControls ?? 0,
      partialControls: s.PartialControls ?? s.partialControls ?? 0,
      totalControls: s.TotalControls ?? s.totalControls ?? 0,
      lastEvaluatedAt: s.LastEvaluatedAt ?? s.lastEvaluatedAt,
      isEnabled: s.IsEnabled ?? s.isEnabled ?? true,
      includeInGuardianScore: s.IncludeInGuardianScore ?? s.includeInGuardianScore ?? true
    }))

    // Create display-friendly framework scores alias for UI components
    const frameworkScores: FrameworkScoreDisplay[] = standardScores.map((s) => ({
      standardId: s.standardId,
      code: s.standardCode,
      name: s.standardName,
      score: s.weightedScore,  // Canonical BPA score (criticality-weighted) — see commit b6c1e07
      passedControls: s.passedControls,
      failedControls: s.failedControls,
      totalControls: s.totalControls,
      waivedControls: s.waivedControls,
      dataUnavailableControls: s.dataUnavailableControls,
      partialControls: s.partialControls,
      weight: s.weight,
      includeInGuardianScore: s.includeInGuardianScore,
      lastEvaluatedAt: s.lastEvaluatedAt
    }))

    // Extract collection health if present (Track C)
    const rawHealth = (data.CollectionHealth ?? data.collectionHealth) as CollectionHealthWire[] | undefined
    const collectionHealth = rawHealth ? rawHealth.map(normalizeCollectionHealth) : undefined

    return {
      tenantId: (data.TenantId as string) ?? (data.tenantId as string) ?? tenantId,
      overallScore: (data.OverallScore as number) ?? (data.overallScore as number) ?? 0,
      totalPassedControls: (data.TotalPassedControls as number) ?? (data.totalPassedControls as number) ?? 0,
      totalFailedControls: (data.TotalFailedControls as number) ?? (data.totalFailedControls as number) ?? 0,
      totalWaivedControls: (data.TotalWaivedControls as number) ?? (data.totalWaivedControls as number) ?? 0,
      totalControls: (data.TotalControls as number) ?? (data.totalControls as number) ?? 0,
      lastEvaluatedAt: (data.LastEvaluatedAt as string) ?? (data.lastEvaluatedAt as string),
      standardScores,
      frameworkScores,  // Alias for backward compatibility
      // Compute violation counts from failed controls for UI display
      totalViolations: (data.TotalFailedControls as number) ?? (data.totalFailedControls as number) ?? 0,
      collectionHealth,
    }
  } catch (error: unknown) {
    if (axios.isAxiosError(error) && error.response?.status === 404) {
      return null
    }
    throw error
  }
}

interface ScoreHistoryItemWire {
  snapshotDate: string
  score: number
  passedControls?: number
  totalControls?: number
  standardId?: string
  standardCode?: string
}
interface ScoreHistoryResponseWire {
  tenantId: string
  overallHistory: ScoreHistoryItemWire[]
  standardHistory: Record<string, ScoreHistoryItemWire[]>
}

/**
 * Get score history for trend charts
 * Transforms backend ScoreHistoryResponseDto to frontend ComplianceTrendPoint[]
 */
export async function getComplianceScoreHistory(tenantId: string, days: number = 90): Promise<ComplianceTrendPoint[]> {
  try {
    // Backend returns ScoreHistoryResponseDto with overallHistory and standardHistory
    const response = await apiClient.get<ApiResponse<ScoreHistoryResponseWire>>(
      `/compliance/tenant/${tenantId}/scores/history`,
      { params: { days } }
    )

    const data = response.data.data || response.data
    if (!data || !data.overallHistory) {
      return []
    }

    // Transform to ComplianceTrendPoint[] format
    // Group by date, combining overall score with framework scores
    const dateMap = new Map<string, ComplianceTrendPoint>()

    // Add overall scores first
    for (const item of data.overallHistory) {
      const dateKey = item.snapshotDate.split('T')[0] // Normalize to date only
      dateMap.set(dateKey, {
        date: item.snapshotDate,
        overallScore: Number(item.score),
        frameworkScores: []
      })
    }

    // Add framework scores
    if (data.standardHistory) {
      for (const [code, history] of Object.entries(data.standardHistory)) {
        for (const item of history) {
          const dateKey = item.snapshotDate.split('T')[0]
          const point = dateMap.get(dateKey)
          if (point) {
            point.frameworkScores.push({
              code: code,
              score: Number(item.score)
            })
          }
        }
      }
    }

    // Convert to array and sort by date
    return Array.from(dateMap.values()).sort(
      (a, b) => new Date(a.date).getTime() - new Date(b.date).getTime()
    )
  } catch (error: unknown) {
    console.error('Failed to fetch score history:', error)
    return []
  }
}

/**
 * Get raw per-evaluation score events for a tenant (no day-bucket collapse).
 * Backend returns newest-first with deltas computed server-side against the
 * immediately prior event; the oldest event in window has delta = null.
 */
export async function getComplianceScoreEvents(tenantId: string, limit: number = 20): Promise<ScoreEvent[]> {
  try {
    const response = await apiClient.get<ApiResponse<ScoreEvent[]>>(
      `/compliance/tenant/${tenantId}/scores/events`,
      { params: { limit } }
    )
    const data = response.data.data ?? response.data
    return Array.isArray(data) ? data : []
  } catch (error: unknown) {
    if (axios.isAxiosError(error) && error.response?.status === 404) {
      return []
    }
    throw error
  }
}

/**
 * Get per-control change feed for a tenant — one row per policy flip
 * (Pass↔Fail, waiver applied, waiver lapsed) since the prior evaluation.
 * Backend returns newest-first with per-row signed `scoreDelta` computed
 * server-side; consumers must not re-sort or recompute.
 */
export async function getControlChanges(tenantId: string, limit: number = 20): Promise<ControlChange[]> {
  try {
    const response = await apiClient.get<ApiResponse<ControlChange[]>>(
      `/compliance/tenant/${tenantId}/control-changes`,
      { params: { limit } }
    )
    const data = response.data.data ?? response.data
    return Array.isArray(data) ? data : []
  } catch (error: unknown) {
    if (axios.isAxiosError(error) && error.response?.status === 404) {
      return []
    }
    throw error
  }
}

// --- Violation Summary ---

/**
 * Get violation summary counts for a tenant (from dedicated summary endpoint).
 * Returns status counts, criticality/framework/workload breakdowns, and collection health.
 *
 * @param options.onlyStarred When false, the server includes findings from
 *   unstarred frameworks (matches the violations list endpoint's `onlyStarred=false`
 *   behavior). When true or undefined, the param is omitted and the server defaults
 *   to starred-only — keeps cache keys stable for the common case.
 */
export async function getViolationSummary(
  tenantId: string,
  options?: { onlyStarred?: boolean }
): Promise<ViolationSummaryResponse | null> {
  try {
    const url = options?.onlyStarred === false
      ? `/compliance/tenant/${tenantId}/violations/summary?onlyStarred=false`
      : `/compliance/tenant/${tenantId}/violations/summary`
    const response = await apiClient.get<ApiResponse<Record<string, unknown>>>(url)
    const data = response.data.data || response.data
    if (!data) return null

    return {
      tenantId: (data.TenantId as string) ?? (data.tenantId as string) ?? tenantId,
      open: (data.Open as number) ?? (data.open as number) ?? 0,
      waived: (data.Waived as number) ?? (data.waived as number) ?? 0,
      resolved: (data.Resolved as number) ?? (data.resolved as number) ?? 0,
      pass: (data.Pass as number) ?? (data.pass as number) ?? 0,
      byCriticality: (data.ByCriticality as Record<string, number>) ?? (data.byCriticality as Record<string, number>) ?? {},
      byFramework: (data.ByFramework as Record<string, number>) ?? (data.byFramework as Record<string, number>) ?? {},
      byWorkload: (data.ByWorkload as Record<string, number>) ?? (data.byWorkload as Record<string, number>) ?? {},
      totalControls: (data.TotalControls as number) ?? (data.totalControls as number) ?? 0,
      collectionHealth: (((data.CollectionHealth ?? data.collectionHealth) as CollectionHealthWire[] | undefined) ?? [])
        .map(normalizeCollectionHealth),
    }
  } catch (error: unknown) {
    if (axios.isAxiosError(error) && error.response?.status === 404) {
      return null
    }
    throw error
  }
}

// --- Violations ---

/**
 * Get violations for a tenant with optional filtering
 * Transforms frontend filter params to match backend ViolationFilterDto
 */
export async function getComplianceViolations(tenantId: string, filters?: ViolationFilters): Promise<PaginatedResponse<ComplianceViolation>> {
  try {
    // Transform filter params to match backend ViolationFilterDto
    // Backend uses PageNumber/PageSize, frontend may pass pageNumber/pageSize
    // onlyStarred is omitted when undefined so the server default
    // (starred-only) applies; pass false explicitly to include unstarred
    // frameworks (e.g., the Violations page "Include unstarred frameworks" toggle).
    const backendParams = filters ? {
      status: filters.status,
      criticality: filters.criticality,
      category: filters.category,
      product: filters.product,
      standardId: filters.standardId,
      ...(filters.onlyStarred !== undefined ? { onlyStarred: filters.onlyStarred } : {}),
      pageNumber: filters.pageNumber ?? 1,
      pageSize: filters.pageSize ?? 50
    } : undefined

    const response = await apiClient.get<ApiResponse<PaginatedResponse<ComplianceViolation>>>(
      `/compliance/tenant/${tenantId}/violations`,
      { params: backendParams }
    )
    const data = response.data.data || response.data

    return {
      items: data?.items ?? [],
      total: data?.total ?? 0,
      pageNumber: data?.pageNumber ?? 1,
      pageSize: data?.pageSize ?? 50,
      totalPages: data?.totalPages ?? 0
    }
  } catch (error: unknown) {
    console.error('Failed to fetch violations:', error)
    return { items: [], total: 0, pageNumber: 1, pageSize: 50, totalPages: 0 }
  }
}

/**
 * Get a specific violation by ID
 */
export async function getComplianceViolation(tenantId: string, violationId: string): Promise<ComplianceViolation | null> {
  try {
    const response = await apiClient.get<ApiResponse<ComplianceViolation>>(
      `/compliance/tenant/${tenantId}/violations/${violationId}`
    )
    return response.data.data || response.data || null
  } catch (error: unknown) {
    if (axios.isAxiosError(error) && error.response?.status === 404) {
      return null
    }
    throw error
  }
}

// --- Waivers ---

/**
 * Get all waivers for a tenant
 */
export async function getComplianceWaivers(tenantId: string): Promise<TenantPolicyWaiver[]> {
  try {
    const response = await apiClient.get<ApiResponse<TenantPolicyWaiver[]>>(`/compliance/tenant/${tenantId}/waivers`)
    return response.data.data || response.data || []
  } catch (error: unknown) {
    console.error('Failed to fetch waivers:', error)
    return []
  }
}

/**
 * Create a waiver for a policy violation
 */
export async function createComplianceWaiver(tenantId: string, data: CreateWaiverRequest): Promise<TenantPolicyWaiver> {
  const response = await apiClient.post<ApiResponse<TenantPolicyWaiver>>(
    `/compliance/tenant/${tenantId}/waivers`,
    data
  )
  return response.data.data || response.data
}

/**
 * Delete (deactivate) a waiver
 */
export async function deleteComplianceWaiver(tenantId: string, waiverId: string): Promise<void> {
  await apiClient.delete(`/compliance/tenant/${tenantId}/waivers/${waiverId}`)
}

/**
 * Update an existing waiver
 */
export async function updateComplianceWaiver(tenantId: string, waiverId: string, data: UpdateWaiverRequest): Promise<TenantPolicyWaiver> {
  const response = await apiClient.put<ApiResponse<TenantPolicyWaiver>>(
    `/compliance/tenant/${tenantId}/waivers/${waiverId}`,
    data
  )
  return response.data.data || response.data
}

// --- OPA Health ---

/**
 * Get OPA service health status. Backend returns camelCase via OpaHealthDto.
 */
export async function getOpaHealth(): Promise<OpaHealthStatus> {
  try {
    const response = await apiClient.get<ApiResponse<OpaHealthStatus>>('/opa/health')
    const data = response.data.data || response.data

    return {
      isHealthy: data?.isHealthy ?? false,
      opaVersion: data?.opaVersion ?? '',
      checkedAt: data?.checkedAt ?? new Date().toISOString(),
      loadedBundles: data?.loadedBundles ?? [],
      errorMessage: data?.errorMessage
    }
  } catch (error: unknown) {
    console.error('Failed to fetch OPA health:', error)
    return {
      isHealthy: false,
      opaVersion: '',
      loadedBundles: [],
      errorMessage: error instanceof Error ? error.message : 'Unknown error',
      checkedAt: new Date().toISOString()
    }
  }
}

export const complianceApi = {
  getComplianceStandards,
  getComplianceStandard,
  getStandardPolicies,
  getTenantStandards,
  assignTenantStandard,
  updateTenantStandard,
  bulkUpdateTenantStandards,
  toggleStandardIncludeInScore,
  triggerComplianceEvaluation,
  getComplianceEvaluations,
  getComplianceEvaluation,
  getComplianceScores,
  getComplianceScoreHistory,
  getComplianceScoreEvents,
  getControlChanges,
  getViolationSummary,
  getComplianceViolations,
  getComplianceViolation,
  getComplianceWaivers,
  createComplianceWaiver,
  deleteComplianceWaiver,
  updateComplianceWaiver,
  getOpaHealth,
}
